import React, { useState } from "react";
import axios from "axios";
import { PROFILE_LABELS } from "../lib/fitScore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STACK_OPTIONS = [
  { value: "no_code",      icon: "🧩", desc: "Bubble, Webflow, Zapier, Notion" },
  { value: "indie_hacker", icon: "🚀", desc: "Solo dev, ships fast, full ownership" },
  { value: "full_stack",   icon: "⚙️", desc: "Comfortable building anything end-to-end" },
  { value: "team",         icon: "🤝", desc: "2+ developers, parallel workstreams" },
];
const BUDGET_OPTIONS = [
  { value: "under_500",  icon: "🪙" },
  { value: "500_to_5k",  icon: "💵" },
  { value: "over_5k",    icon: "💼" },
];
const HOURS_OPTIONS = [
  { value: "under_5",  icon: "🌙" },
  { value: "5_to_15",  icon: "⏳" },
  { value: "over_15",  icon: "🔥" },
];

const OptionCard = ({ option, group, selected, onSelect, testid }) => {
  const label = PROFILE_LABELS[group][option.value];
  return (
    <button
      type="button"
      onClick={() => onSelect(option.value)}
      data-testid={testid}
      data-selected={selected ? "true" : "false"}
      className={`text-left w-full rounded-xl ring-1 px-4 py-3 transition ${
        selected
          ? "ring-violet-400 bg-violet-50/80 shadow-[0_0_0_3px_rgba(139,92,246,0.12)]"
          : "ring-zinc-200 bg-white hover:ring-zinc-300 hover:bg-zinc-50"
      }`}
    >
      <div className="flex items-center gap-3">
        <span className="text-xl leading-none" aria-hidden>{option.icon}</span>
        <div className="min-w-0">
          <div className={`text-[13px] font-semibold ${selected ? "text-violet-800" : "text-zinc-900"}`}>{label}</div>
          {option.desc && (
            <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{option.desc}</div>
          )}
        </div>
      </div>
    </button>
  );
};

export default function FounderProfileModal({ open, onClose, onSaved }) {
  const [stack, setStack] = useState(null);
  const [budget, setBudget] = useState(null);
  const [hours, setHours] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  if (!open) return null;
  const canSave = stack && budget && hours && !saving;

  const close = () => {
    setErr(null);
    onClose?.();
  };

  const save = async () => {
    if (!canSave) return;
    const token = localStorage.getItem("p2p_token");
    if (!token) { close(); return; }
    setSaving(true);
    setErr(null);
    try {
      const r = await axios.put(
        `${API}/me/profile`,
        { stack, budget, hours_per_week: hours },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 },
      );
      onSaved?.(r.data.profile);
      close();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const skip = async () => {
    const token = localStorage.getItem("p2p_token");
    if (!token) { close(); return; }
    setSaving(true);
    setErr(null);
    try {
      await axios.post(
        `${API}/me/profile/skip`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 },
      );
      onSaved?.({ skipped: true });
      close();
    } catch (e) {
      setErr("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-zinc-900/40 backdrop-blur-sm p-3 sm:p-6 overflow-y-auto"
      data-testid="founder-profile-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl ring-1 ring-zinc-200 my-4 sm:my-0">
        {/* Header */}
        <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-zinc-100">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-600 mb-1">
            3-question setup · ~20 seconds
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-zinc-900 leading-tight">
            Tailor the feed to <span className="text-violet-700">your</span> reality
          </h2>
          <p className="mt-1.5 text-[13px] text-zinc-600 leading-snug">
            We'll re-rank opportunities by how well they match your stack, budget, and weekly hours. You can change or skip this any time.
          </p>
        </div>

        {/* Questions */}
        <div className="px-6 sm:px-8 py-5 space-y-5 max-h-[60vh] sm:max-h-none overflow-y-auto">
          <section>
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">1 · Your stack</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {STACK_OPTIONS.map((o) => (
                <OptionCard
                  key={o.value}
                  option={o}
                  group="stack"
                  selected={stack === o.value}
                  onSelect={setStack}
                  testid={`founder-profile-stack-${o.value}`}
                />
              ))}
            </div>
          </section>

          <section>
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">2 · Build budget</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {BUDGET_OPTIONS.map((o) => (
                <OptionCard
                  key={o.value}
                  option={o}
                  group="budget"
                  selected={budget === o.value}
                  onSelect={setBudget}
                  testid={`founder-profile-budget-${o.value}`}
                />
              ))}
            </div>
          </section>

          <section>
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">3 · Hours you can ship per week</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {HOURS_OPTIONS.map((o) => (
                <OptionCard
                  key={o.value}
                  option={o}
                  group="hours_per_week"
                  selected={hours === o.value}
                  onSelect={setHours}
                  testid={`founder-profile-hours-${o.value}`}
                />
              ))}
            </div>
          </section>

          {err && (
            <div className="rounded-lg bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] px-3 py-2" data-testid="founder-profile-error">
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-8 py-4 border-t border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={skip}
            disabled={saving}
            className="text-[12.5px] font-semibold text-zinc-500 hover:text-zinc-800 underline decoration-dotted disabled:opacity-50"
            data-testid="founder-profile-skip-btn"
          >
            Skip — show me everything
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={close}
              disabled={saving}
              className="text-[12.5px] font-semibold px-3 py-2 text-zinc-600 hover:text-zinc-900 disabled:opacity-50"
              data-testid="founder-profile-cancel-btn"
            >
              Later
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!canSave}
              className={`text-[13px] font-semibold px-4 py-2 rounded-full transition ${canSave ? "bg-violet-600 text-white hover:bg-violet-700" : "bg-zinc-200 text-zinc-400 cursor-not-allowed"}`}
              data-testid="founder-profile-save-btn"
            >
              {saving ? "Saving…" : "Save & personalize"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
